/**
 * ProductCard Component
 * Displays product in grid/list views with quick add functionality
 */

import { useState } from 'react';
import { motion } from 'motion/react';
import type { Product } from '~/lib/shopify/types';
import { formatPrice, calculateDiscount, getProductUrl } from '~/lib/shopify/api';
import { useCartStore } from '~/store/cart-store';
import { trackSelectItem } from '~/lib/tracking/gtm';

interface ProductCardProps {
  product: Product;
  priority?: boolean;
  index?: number;
  listName?: string;
}

export function ProductCard({ product, priority = false, index = 0, listName }: ProductCardProps) {
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const addItem = useCartStore((state) => state.addItem);

  const variants = product.variants.edges.map((e) => e.node);
  const selectedVariant = variants[selectedVariantIndex];
  const price = selectedVariant?.price || product.priceRange.minVariantPrice;
  const compareAtPrice = selectedVariant?.compareAtPrice;
  const hasDiscount = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
  const discountPercent = hasDiscount ? calculateDiscount(price.amount, compareAtPrice.amount) : 0;

  // Get available options (e.g., USB-A / USB-C)
  const hasOptions = product.options.length > 0 && product.options[0].values.length > 1;
  const connectionOptions = product.options.find(
    (o) => o.name.toLowerCase().includes('usb') || o.name.toLowerCase().includes('connection')
  );

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedVariant || !product.availableForSale) return;

    setIsAdding(true);
    try {
      await addItem(selectedVariant.id, 1);
    } finally {
      setIsAdding(false);
    }
  };

  const handleVariantChange = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVariantIndex(index);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="product-card group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <a
        href={getProductUrl(product.handle)}
        className="block"
        onClick={() => { if (listName) trackSelectItem(product, index, listName); }}
      >
        {/* Image Container */}
        <div className="product-card-image relative">
          {/* Badges */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
            {hasDiscount && <span className="badge-sale">-{discountPercent}%</span>}
            {product.tags.includes('new') && <span className="badge-new">New</span>}
            {product.tags.includes('refurbished') && <span className="bg-amber-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">Refurbished</span>}
            {!product.availableForSale && <span className="badge-sold-out">Sold Out</span>}
          </div>

          {/* Brand Badge */}
          <div className="absolute top-3 right-3 z-10">
            <span className="badge-brand">{product.vendor}</span>
          </div>

          {/* Product Image */}
          {product.featuredImage ? (
            <div className="w-full h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-xl w-full h-full flex items-center justify-center overflow-hidden">
                <img
                  src={product.featuredImage.url}
                  alt={product.featuredImage.altText || product.title}
                  loading={priority ? 'eager' : 'lazy'}
                  className="w-full h-full object-contain p-4 transition-transform duration-500 ease-out-expo group-hover:scale-105"
                />
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-20 h-20 text-surface-ash" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
              </svg>
            </div>
          )}

          {/* Quick Add Button */}
          <motion.div
            initial={false}
            animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-4 left-4 right-4 z-10"
          >
            <button
              onClick={handleQuickAdd}
              disabled={!product.availableForSale || isAdding}
              className="btn-primary w-full py-2.5 text-xs"
            >
              {isAdding ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Adding...
                </span>
              ) : product.availableForSale ? (
                'Quick Add'
              ) : (
                'Out of Stock'
              )}
            </button>
          </motion.div>
        </div>

        {/* Product Info */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-medium text-accent-white text-sm line-clamp-2 mb-2 group-hover:text-brand-orange transition-colors">
            {product.title}
          </h3>

          {/* Variant Options */}
          {connectionOptions && connectionOptions.values.length > 1 && (
            <div className="flex gap-1.5 mb-3">
              {connectionOptions.values.map((value, i) => {
                const variantIndex = variants.findIndex((v) =>
                  v.selectedOptions.some((o) => o.value === value)
                );
                const isSelected = selectedVariantIndex === variantIndex;

                return (
                  <button
                    key={value}
                    onClick={(e) => handleVariantChange(e, variantIndex >= 0 ? variantIndex : 0)}
                    className={`px-2 py-1 text-2xs font-mono rounded-md transition-colors ${
                      isSelected
                        ? 'bg-brand-orange text-white'
                        : 'bg-surface-slate text-accent-silver hover:bg-surface-ash'
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          )}

          {/* Price */}
          <div>
            <div className="flex items-baseline gap-2">
              <span className={`price text-lg ${hasDiscount ? 'price-sale' : ''}`}>
                {formatPrice(price.amount, price.currencyCode)}
              </span>
              {hasDiscount && compareAtPrice && (
                <span className="price-original text-sm">
                  {formatPrice(compareAtPrice.amount, compareAtPrice.currencyCode)}
                </span>
              )}
            </div>
            <span className="text-xs text-accent-silver">inc. VAT</span>
          </div>

          {/* SKU indicator */}
          {selectedVariant?.sku && (
            <div className="mt-2">
              <span className="text-2xs text-accent-silver font-mono">
                {selectedVariant.sku}
              </span>
            </div>
          )}
        </div>
      </a>
    </motion.article>
  );
}
